import Parse from 'parse';

import { Injectable } from '@angular/core';

import {
  SampleTopology,
  ProtocolPipelineTopology,
  ObjectTopology,
  PipelineTopology,
  PipelineStepTopology,
  ParseBaseRepository,
} from '@telespot/infrastructure/parse';

import { CategorizationItem, Pipeline, Step } from '../state';
import { type PipelineRepository } from '@telespot/protocols/domain';
import { PipelineEnvironment } from '@telespot/domain';

@Injectable({
  providedIn: 'root',
})
export class TelespotPipelineService extends ParseBaseRepository implements PipelineRepository {
  constructor() {
    super(Parse, { useMasterKey: false });
  }

  public findForSample(sampleId: string): Promise<Pipeline[]> {
    const sampleQuery = new Parse.Query(SampleTopology.TABLE).equalTo(ObjectTopology.ID, sampleId);

    const protocolPipelinesQuery = new Parse.Query(ProtocolPipelineTopology.TABLE).matchesKeyInQuery(
      ProtocolPipelineTopology.METHOD_TYPE,
      SampleTopology.METHOD_TYPE,
      sampleQuery
    );

    const pipelinesQuery = new Parse.Query(PipelineTopology.TABLE)
      .matchesKeyInQuery(
        ObjectTopology.ID,
        [ProtocolPipelineTopology.PIPELINE, ObjectTopology.ID].join('.'),
        protocolPipelinesQuery
      )
      .equalTo(PipelineTopology.ENV, PipelineEnvironment.CLOUD);

    const pipelines = this.getTokenPaginationGenerator({ query: pipelinesQuery, limit: 50 });

    return this.toPipelines(pipelines);
  }

  public findStepsForSample(
    sampleId: string
  ): Promise<{ steps: Step[]; categories: Record<string, CategorizationItem> }> {
    const sampleQuery = new Parse.Query(SampleTopology.TABLE).equalTo(ObjectTopology.ID, sampleId);

    // Filter to only cloud pipelines
    const cloudPipelinesQuery = new Parse.Query(PipelineTopology.TABLE).equalTo(
      PipelineTopology.ENV,
      PipelineEnvironment.CLOUD
    );

    // Combine method-type filter AND cloud filter in the same intermediate query.
    // This keeps stepsQuery with a single matchesKeyInQuery — two parallel nested
    // subqueries on the same query fail silently in the Parse JS SDK.
    const protocolPipelinesQuery = new Parse.Query(ProtocolPipelineTopology.TABLE)
      .matchesKeyInQuery(ProtocolPipelineTopology.METHOD_TYPE, SampleTopology.METHOD_TYPE, sampleQuery)
      .matchesQuery(ProtocolPipelineTopology.PIPELINE, cloudPipelinesQuery);

    const stepsQuery = new Parse.Query(PipelineStepTopology.TABLE)
      .matchesKeyInQuery(PipelineStepTopology.PIPELINE, ProtocolPipelineTopology.PIPELINE, protocolPipelinesQuery)
      .ascending(PipelineStepTopology.ORDER);

    const parseSteps = this.getTokenPaginationGenerator({ query: stepsQuery, limit: 50 });

    return this.toStepsAndCategories(parseSteps);
  }

  // Mapper
  private async toPipelines(generator: AsyncGenerator<Parse.Object[]>) {
    const pipelines: Pipeline[] = [];

    for await (const items of generator) {
      for (const item of items) {
        pipelines.push({
          id: item.id,
          names: item.get(PipelineTopology.NAME),
          environment: item.get(PipelineTopology.ENV),
        });
      }
    }

    return pipelines;
  }

  // Mapper
  private async toStepsAndCategories(generator: AsyncGenerator<Parse.Object[]>) {
    const categories: Record<string, CategorizationItem> = {};
    const steps: Step[] = [];

    for await (const items of generator) {
      for (const step of items) {
        steps.push({
          id: step.id,
          stepTask: step.get(PipelineStepTopology.TASK),
          pipelineId: step.get(PipelineStepTopology.PIPELINE)?.id,
          generateFinding: step.get(PipelineStepTopology.GENERATEFINDING),
          trigger: step.get(PipelineStepTopology.TRIGGER),
          order: step.get(PipelineStepTopology.ORDER),
          executorId: step.get(PipelineStepTopology.EXECUTOR)?.id,
          categories: step.get(PipelineStepTopology.PARAMS)?.categorization?.map((c) => c.category) ?? [],
          thresholds: step.get(PipelineStepTopology.PARAMS)?.options,
          allowNewOptions: step.get(PipelineStepTopology.PARAMS)?.allowNewOptions,
          enableMosaicView: step.get(PipelineStepTopology.PARAMS)?.enableMosaicView,
          counter: step.get(PipelineStepTopology.PARAMS)?.counter,
          multiple: step.get(PipelineStepTopology.PARAMS)?.multiple,
        });

        for (const category of step.get(PipelineStepTopology.PARAMS)?.categorization ?? []) {
          categories[category.category] = {
            id: category.category,
            stepId: step.id,
            options: category.options,
          };
        }
      }
    }

    return { steps, categories };
  }
}
